import { ReactNode, useEffect, KeyboardEvent } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { Box, Flex } from 'reflexbox';

import { Link, useHistory } from '@treams/treams-routes';

import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import Breakpoint from 'packages/kakapo-ui-kit/helpers/Breakpoint';
import CloseIcon from 'packages/kakapo-ui-kit/assets/icons/CloseIcon';
import { LinkTo } from 'packages/kakapo-ui-kit/types/Link';

interface IProps {
	rightIconButton?: ReactNode;
	leftIconButton?: ReactNode;
	// Close button is shown while either returnUrl or onCancel is not null
	returnUrl?: LinkTo;
	onCancel?: (event: KeyboardEvent | React.MouseEvent) => void;
	children?: ReactNode;
	small?: boolean;
}

interface IElementProps {
	small?: boolean;
}

const Element = styled(Flex)<IElementProps>`
	background-color: ${(props) => props.theme.colors.lightBlue};
	text-align: center;
	padding: ${({ small }) => (!small ? '24px 10px' : '14px 14px')};
	position: relative;

	@media screen and (max-width: ${Breakpoint.Medium}) {
		padding: 14px 10px;
	}

	${SectionTitle} {
		font-size: 18px;

		@media screen and (max-width: ${Breakpoint.Medium}) {
			font-size: 16px;
		}
	}
`;

export const ModalHeaderTitle = SectionTitle;

class ModalKeyupEventManager {
	static eventHandlers: Array<EventListener> = [];

	static addEventListener(handler: EventListener) {
		this.removeAllEventHandlers();
		this.eventHandlers.push(handler);

		window.addEventListener('keyup', handler);
	}

	static removeEventListener(handler: EventListener) {
		this.removeAllEventHandlers();
		this.eventHandlers = this.eventHandlers.filter((eventHandler) => eventHandler !== handler);
		window.addEventListener('keyup', this.eventHandlers[this.eventHandlers.length - 1]);
	}

	static removeAllEventHandlers() {
		this.eventHandlers.forEach((eventHandler) => {
			window.removeEventListener('keyup', eventHandler);
		});
	}
}

const CloseButton = styled(Link)<IProps>`
	background-color: ${(props) => props.theme.colors.white};
	height: 40px;
	width: 40px;
	border-radius: 20px;
	line-height: 50px;
	text-align: center;
	display: inline-block;

	@media screen and (max-width: ${Breakpoint.Medium}) {
		width: 30px;
		height: 30px;
		line-height: 40px;
		border-radius: 15px;

		svg {
			max-width: 12px;
		}
	}
`;

export default function ModalHeader({
	rightIconButton,
	leftIconButton,
	returnUrl,
	onCancel,
	children,
	small = true,
}: IProps) {
	const history = useHistory();
	useEffect(() => {
		// If escape is pressed, direct user to the returnUrl
		const handleKeyUp = (event: any) => {
			if (event.key === 'Escape') {
				if (returnUrl != null) {
					history.push(returnUrl);
				}

				if (onCancel != null) {
					onCancel(event);
				}
			}
		};

		// Add event listener on mount and remove on unmount
		ModalKeyupEventManager.addEventListener(handleKeyUp);
		return () => ModalKeyupEventManager.removeEventListener(handleKeyUp);
	}, []);

	return (
		<Element small={small} alignItems="center">
			{leftIconButton}

			<Box flexGrow={1} flexShrink={1}>
				{children}
			</Box>

			{rightIconButton}
			{(returnUrl || onCancel) && (
				<Box ml={3}>
					<CloseButton
						to={returnUrl ?? '#'}
						onClick={(event: React.MouseEvent) => {
							// When returnUrl is null, do not redirect to that URL
							if (returnUrl == null) {
								event.preventDefault();
							}

							if (onCancel != null) {
								onCancel(event);
							}
						}}
					>
						<CloseIcon />
					</CloseButton>
				</Box>
			)}
		</Element>
	);
}
